import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  SearchCheck,
  Package,
  Truck,
  Store,
  Receipt,
  ArrowRight,
  CheckCircle2,
  Eye,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  UserCheck,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { API } from "@/lib/api";

const BUCKET_CONFIG = [
  { key: "New", icon: Package, color: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "Search", icon: SearchCheck, color: "bg-violet-50 text-violet-700 border-violet-300" },
  { key: "Pickup", icon: Truck, color: "bg-blue-50 text-blue-700 border-blue-300" },
  { key: "At Merchant", icon: Store, color: "bg-amber-50 text-amber-700 border-amber-300" },
  { key: "Billing", icon: Receipt, color: "bg-orange-50 text-orange-700 border-orange-300" },
  { key: "Delivery", icon: ArrowRight, color: "bg-cyan-50 text-cyan-700 border-cyan-300" },
  { key: "Closed", icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700 border-emerald-300" },
];

const JOURNEY_LABELS = {
  home_to_shop: "Home → Shop → Home",
  shop_to_home: "Shop → Home",
  c2c: "C2C",
};

const JOURNEY_COLORS = {
  home_to_shop: "bg-blue-100 text-blue-700",
  shop_to_home: "bg-purple-100 text-purple-700",
  c2c: "bg-teal-100 text-teal-700",
};

const SERVICE_TYPES = [
  "Ironing", "Wash and Fold", "Wash and Iron", "Dry Cleaning", "Tailoring", "Other",
];

export const ActiveOrders = () => {
  const [kanban, setKanban] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [rmFilter, setRmFilter] = useState("all");
  const [collapsed, setCollapsed] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newRmName, setNewRmName] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [form, setForm] = useState({
    order_name: "",
    customer_name: "",
    customer_phone: "",
    contact_id: "",
    service_type: "",
    journey_type: "home_to_shop",
    merchant_id: "",
    rm_ids: [],
    plane_url: "",
    notes: "",
    pickup_address: "",
    delivery_address: "",
    pickup_time: "",
    delivery_time: "",
  });

  const fetchKanban = async ({ silent = false } = {}) => {
    try {
      const res = await fetch(`${API}/orders/kanban`);
      const data = await res.json();
      setKanban(data);
      setLastRefreshed(new Date());
    } catch {
      if (!silent) toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchMeta = async () => {
    const [vRes, sRes] = await Promise.all([
      fetch(`${API}/vendors`),
      fetch(`${API}/staff?team=Relations+Manager`),
    ]);
    setVendors(await vRes.json());
    setStaff(await sRes.json());
  };

  useEffect(() => {
    fetchKanban();
    fetchMeta();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (createOpen) return;
      fetchKanban({ silent: true });
    }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, createOpen]);

  const toggleCollapse = (key) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const filteredKanban = () => {
    const filtered = {};
    for (const [bucket, orders] of Object.entries(kanban)) {
      filtered[bucket] = orders.filter((o) => {
        const matchSearch =
          !search.trim() ||
          o.order_number?.toLowerCase().includes(search.toLowerCase()) ||
          o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
          o.merchant_name?.toLowerCase().includes(search.toLowerCase()) ||
          o.customer_phone?.includes(search.trim());
        const matchRM =
          rmFilter === "all" || (o.rm_names || []).includes(rmFilter);
        return matchSearch && matchRM;
      });
    }
    return filtered;
  };

  const createRM = async (name) => {
    if (!name.trim()) return;
    try {
      const res = await fetch(`${API}/staff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ full_name: name.trim(), team: "Relations Manager" }),
      });
      if (!res.ok) throw new Error("Failed");
      const created = await res.json();
      setStaff(prev => [...prev, created]);
      setForm(p => ({ ...p, rm_ids: [...p.rm_ids, created.id] }));
      setNewRmName("");
      toast.success(`RM "${created.full_name}" created`);
    } catch {
      toast.error("Failed to create RM");
    }
  };

  const handleCreate = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name required"); return; }
    if (!form.customer_phone.trim()) { toast.error("Phone required"); return; }
    if (!form.service_type) { toast.error("Service type required"); return; }

    setSaving(true);
    try {
      const payload = { ...form };
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed");
      const order = await res.json();
      toast.success(`Order ${order.order_number} created!`);
      setCreateOpen(false);
      setForm({ order_name: "", customer_name: "", customer_phone: "", contact_id: "", service_type: "", journey_type: "home_to_shop", merchant_id: "", rm_ids: [], plane_url: "", notes: "", pickup_address: "", delivery_address: "", pickup_time: "", delivery_time: "" });
      fetchKanban();
    } catch {
      toast.error("Failed to create order");
    } finally {
      setSaving(false);
    }
  };

  const totalOrders = Object.values(kanban).reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="space-y-5" data-testid="active-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Active Orders</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {totalOrders} order{totalOrders !== 1 ? "s" : ""} in pipeline
            {lastRefreshed && (
              <span className="ml-2 text-slate-400">· Updated {lastRefreshed.toLocaleTimeString()}</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer">
            <Checkbox
              checked={autoRefresh}
              onCheckedChange={(c) => setAutoRefresh(!!c)}
            />
            Auto-refresh 10s
          </label>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchKanban(); }}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${autoRefresh ? "animate-[spin_10s_linear_infinite]" : ""}`} /> Refresh
          </Button>
          <Button size="sm" className="bg-[#0047FF] hover:bg-[#0033CC]" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> New Order
          </Button>
        </div>
      </div>

      {/* Search + RM Filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <UserCheck className="w-4 h-4 text-slate-400" />
          <Select value={rmFilter} onValueChange={setRmFilter}>
            <SelectTrigger className="h-9 text-sm w-44">
              <SelectValue placeholder="All RMs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All RMs</SelectItem>
              {staff.map((s) => (
                <SelectItem key={s.id} value={s.full_name}>{s.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-pulse text-slate-500">Loading orders...</div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {BUCKET_CONFIG.map(({ key, icon: Icon, color }) => {
            const orders = (filteredKanban()[key] || []);
            const isCollapsed = !!collapsed[key];
            return (
              <div key={key} className="space-y-2">
                {/* Bucket header — clickable to collapse */}
                <button
                  onClick={() => toggleCollapse(key)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-opacity ${color} ${isCollapsed ? "opacity-70" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wide">{key}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono font-bold">{orders.length}</span>
                    {isCollapsed
                      ? <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                      : <ChevronUp className="w-3.5 h-3.5 opacity-60" />}
                  </div>
                </button>

                {/* Order cards — hidden when collapsed */}
                {!isCollapsed && (
                  <div className="space-y-2 min-h-[60px]">
                    {orders.length === 0 ? (
                      <div className="text-center text-xs text-slate-400 py-6">No orders</div>
                    ) : (
                      orders.map((order) => (
                        <Link key={order.id} to={`/orders/${order.id}`}>
                          <Card className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer mb-2">
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between mb-1">
                                <span className="font-mono text-xs font-semibold text-slate-700">{order.order_number}</span>
                                <Eye className="w-3.5 h-3.5 text-slate-400" />
                              </div>
                              {order.order_name && (
                                <p className="text-sm font-semibold text-slate-900 truncate">{order.order_name}</p>
                              )}
                              <p className={`text-sm ${order.order_name ? "text-slate-500" : "font-medium text-slate-900"} truncate`}>{order.customer_name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{order.service_type}</p>
                              {order.merchant_name && (
                                <p className="text-xs text-slate-400 mt-0.5 truncate">{order.merchant_name}</p>
                              )}
                              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${JOURNEY_COLORS[order.journey_type] || "bg-slate-100 text-slate-600"}`}>
                                  {JOURNEY_LABELS[order.journey_type] || order.journey_type}
                                </span>
                                {(order.rm_names || []).map((name, i) => (
                                  <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{name}</span>
                                ))}
                                {order.pickup_rider_name && (
                                  <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                                    <Truck className="w-2.5 h-2.5" /> {order.pickup_rider_name}
                                  </span>
                                )}
                                {order.delivery_rider_name && order.delivery_rider_name !== order.pickup_rider_name && (
                                  <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                                    <Truck className="w-2.5 h-2.5" /> {order.delivery_rider_name}
                                  </span>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Order Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Create New Order</DialogTitle>
            <DialogDescription>Enter customer and service details to start an order.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Order Name</Label>
              <Input value={form.order_name} onChange={(e) => setForm(p => ({ ...p, order_name: e.target.value }))} placeholder="e.g. Anika's Ironing Pickup" className="h-9 text-sm" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Customer Name *</Label>
                <Input value={form.customer_name} onChange={(e) => setForm(p => ({ ...p, customer_name: e.target.value }))} placeholder="Customer name" className="h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone *</Label>
                <Input value={form.customer_phone} onChange={(e) => setForm(p => ({ ...p, customer_phone: e.target.value }))} placeholder="Phone" className="h-9 text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Contact ID</Label>
                <Input value={form.contact_id} onChange={(e) => setForm(p => ({ ...p, contact_id: e.target.value }))} placeholder="Chatwoot ID" className="h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Journey Type *</Label>
                <Select value={form.journey_type} onValueChange={(v) => {
                  setForm(p => {
                    const vendor = vendors.find(vn => vn.id === p.merchant_id);
                    const addr = vendor?.address || "";
                    if (v === "shop_to_home") return { ...p, journey_type: v, pickup_address: addr };
                    if (v === "home_to_shop") return { ...p, journey_type: v, delivery_address: addr };
                    return { ...p, journey_type: v };
                  });
                }}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home_to_shop">Home → Shop → Home</SelectItem>
                    <SelectItem value="shop_to_home">Shop → Home</SelectItem>
                    <SelectItem value="c2c">C2C (Customer to Customer)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Service Type *</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm(p => ({ ...p, service_type: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Merchant</Label>
                <Select value={form.merchant_id} onValueChange={(v) => {
                  const vendor = vendors.find(vn => vn.id === v);
                  setForm(p => {
                    const jt = p.journey_type || "home_to_shop";
                    const addr = vendor?.address || "";
                    if (jt === "shop_to_home") return { ...p, merchant_id: v, pickup_address: addr };
                    if (jt === "home_to_shop") return { ...p, merchant_id: v, delivery_address: addr };
                    return { ...p, merchant_id: v };
                  });
                }}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select merchant" /></SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.merchant_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Assigned RMs</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-9 text-sm justify-start font-normal">
                    {form.rm_ids.length > 0
                      ? form.rm_ids.map(id => staff.find(s => s.id === id)?.full_name).filter(Boolean).join(", ")
                      : <span className="text-muted-foreground">Select RMs...</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                  {staff.map(s => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                      <Checkbox
                        checked={form.rm_ids.includes(s.id)}
                        onCheckedChange={(checked) => {
                          setForm(p => ({
                            ...p,
                            rm_ids: checked ? [...p.rm_ids, s.id] : p.rm_ids.filter(id => id !== s.id),
                          }));
                        }}
                      />
                      {s.full_name}
                    </label>
                  ))}
                  {staff.length === 0 && <p className="text-xs text-slate-400 p-2">No RMs yet</p>}
                  <div className="border-t mt-1 pt-1.5 flex gap-1.5 px-1">
                    <Input
                      value={newRmName}
                      onChange={(e) => setNewRmName(e.target.value)}
                      placeholder="New RM name..."
                      className="h-7 text-xs flex-1"
                      onKeyDown={(e) => e.key === "Enter" && createRM(newRmName)}
                    />
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => createRM(newRmName)} disabled={!newRmName.trim()}>
                      <Plus className="w-3 h-3 mr-0.5" /> Add
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Customer Pickup Address</Label>
              <Input value={form.pickup_address} onChange={(e) => setForm(p => ({ ...p, pickup_address: e.target.value }))} placeholder="Address or Google Maps link" className="h-9 text-sm" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Drop Address (Customer / Vendor)</Label>
              <Input value={form.delivery_address} onChange={(e) => setForm(p => ({ ...p, delivery_address: e.target.value }))} placeholder="Drop-off address or Google Maps link" className="h-9 text-sm" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Pickup Time</Label>
                <Input type="datetime-local" value={form.pickup_time} onChange={(e) => setForm(p => ({ ...p, pickup_time: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Delivery Time</Label>
                <Input type="datetime-local" value={form.delivery_time} onChange={(e) => setForm(p => ({ ...p, delivery_time: e.target.value }))} className="h-9 text-sm" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Plane URL</Label>
              <Input value={form.plane_url} onChange={(e) => setForm(p => ({ ...p, plane_url: e.target.value }))} placeholder="https://..." className="h-9 text-sm" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes..." className="h-9 text-sm" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#0047FF] hover:bg-[#0033CC]">
              {saving ? "Creating..." : "Create Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
